import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Assignment as TemplateIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const ProgramTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTemplates, setTotalTemplates] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || '';

    const axiosConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    };

    const fetchTemplates = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: '10',
                ...(search && { name: search })
            });

            const url = `${API_URL}/api/program-templates?${queryParams}`;
            const response = await axios.get(url, axiosConfig);
            const result = response.data;

            if (result.success) {
                setTemplates(result.data);
                setTotalPages(result.pagination.totalPages);
                setCurrentPage(result.pagination.currentPage);
                setTotalTemplates(result.pagination.totalTemplates);
            } else {
                handleError(result.message || 'Failed to fetch program templates');
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            handleError('Failed to fetch program templates');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (template) => {
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!templateToDelete) return;

        try {
            const url = `${API_URL}/api/program-templates/${templateToDelete._id}`;
            const response = await axios.delete(url, axiosConfig);
            const result = response.data;

            if (result.success) {
                handleSuccess('Program template deleted successfully');
                fetchTemplates(currentPage, searchTerm);
            } else {
                handleError(result.message || 'Failed to delete template');
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            handleError('Failed to delete template');
        }

        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchTemplates(1, searchTerm);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setCurrentPage(1);
        fetchTemplates(1);
    };

    const handlePageChange = (event, page) => {
        fetchTemplates(page, searchTerm);
    };

    const formatAgeRange = (ageRange) => {
        if (!ageRange || ageRange.length === 0) return 'Not specified';
        
        const ages = ageRange.map(age => age.age).sort((a, b) => a - b);
        
        if (ages.length === 1) return `${ages[0]} years`;
        if (ages.length === 2) return `${ages[0]}-${ages[1]} years`;
        return `${ages[0]}-${ages[ages.length - 1]} years`;
    };

    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="400px"
                sx={{ 
                    marginLeft: '320px', // Fixed left margin accounting for sidebar width + gap
                    marginRight: '24px',
                    marginTop: '24px',
                    padding: '20px'
                }}
            >
                <CircularProgress size={40} />
                <Typography variant="h6" sx={{ ml: 2 }}>Loading templates...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            marginLeft: '90px',
            marginRight: '24px',  
            marginTop: '24px',   
            marginBottom: '24px', 
            padding: '20px',
            minHeight: '100vh', 
            boxSizing: 'border-box'
        }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight={700} color="primary.main">
                        <TemplateIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        Programs
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mt={1}>
                        {totalTemplates} Programs found
                    </Typography>
                </Box>

                <Button
                    component={Link}
                    to="/program-templates/create"
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    ADD NEW Program
                </Button>
            </Box>

            {/* Search */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Search Program</Typography>
                <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        placeholder="Search by Program name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: 1 }}
                        size="small"
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            minWidth: 100,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        SEARCH
                    </Button>
                    <Button
                        onClick={clearFilters}
                        variant="outlined"
                        sx={{
                            minWidth: 80,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        CLEAR
                    </Button>
                </Box>
            </Paper>

            {/* Templates Table */}
            {templates.length === 0 ? (
                <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>No Program found</Typography>
                    <Typography variant="body1" color="text.secondary" mb={3}>
                        {searchTerm 
                            ? 'Try adjusting your search criteria'
                            : 'Get started by creating your first program program'
                        }
                    </Typography>
                    <Button
                        component={Link}
                        to="/program-templates/create"
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        Create Your First program
                    </Button>
                </Paper>
            ) : (
                <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 16, py: 2 }}>
                                        Program Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 16, py: 2 }}>
                                        <Box display="flex" alignItems="center">
                                            <PeopleIcon sx={{ mr: 1, fontSize: 18 }} />
                                            Age Range
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 16, py: 2 }}>
                                        Created Date
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 16, py: 2 }} align="center">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates.map((template) => (
                                    <TableRow 
                                        key={template._id}
                                        sx={{ 
                                            '&:hover': { 
                                                backgroundColor: '#f9f9f9' 
                                            },
                                            '&:last-child td, &:last-child th': { border: 0 }
                                        }}
                                    >
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center">
                                                <TemplateIcon sx={{ mr: 2, color: 'primary.main' }} />
                                                <Typography variant="body1" fontWeight={600}>
                                                    {template.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Chip
                                                label={formatAgeRange(template.ageRange)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'primary.light',
                                                    color: 'white',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date(template.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ py: 2 }}>
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Tooltip title="Edit Template">
                                                    <IconButton
                                                        component={Link}
                                                        to={`/program-templates/edit/${template._id}`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            width: 32,
                                                            height: 32,
                                                            '&:hover': {
                                                                backgroundColor: 'primary.dark',
                                                                transform: 'scale(1.1)'
                                                            },
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                <Tooltip title="Delete Template">
                                                    <IconButton
                                                        onClick={() => handleDeleteClick(template)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'error.main',
                                                            color: 'white',
                                                            width: 32,
                                                            height: 32,
                                                            '&:hover': {
                                                                backgroundColor: 'error.dark',
                                                                transform: 'scale(1.1)'
                                                            },
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" p={3}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                programName={templateToDelete?.name}
            />

            <ToastContainer />
        </Box>
    );
};

export default ProgramTemplates;
